import { useEffect, useState, useMemo } from "react";
import AuthUser from "./AuthUser";
import TaskSkeleton from "./TaskSkeleton";
import moment from "moment";
import { Link } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function downloadCSV(rows, filename) {
  const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const MONTHS = [
  { value: 1, label: "Jan" }, { value: 2, label: "Feb" }, { value: 3, label: "Mar" },
  { value: 4, label: "Apr" }, { value: 5, label: "May" }, { value: 6, label: "Jun" },
  { value: 7, label: "Jul" }, { value: 8, label: "Aug" }, { value: 9, label: "Sep" },
  { value: 10, label: "Oct" }, { value: 11, label: "Nov" }, { value: 12, label: "Dec" },
];

const CUSTOM_ORDER = {
  "BOOPALAN P": 1,
  "AKHIL VA": 2,
  "NAVEEN A": 3,
  "SREE CHARAN M": 4,
  "ARUN KUMAR": 5,
  "ARJUN S": 6,
  "VARUN A": 7,
  "MUKESH P": 8,
  "SIVA KUMAR": 9,
  "SUSHMA S": 10,
  "JAGADESH S": 11,
  "MUKU TU": 12,
  "MANI TU": 13,
};

function getYears() {
  const current = new Date().getFullYear();
  return [current, current - 1, current - 2, current - 3];
}

function getMonthRange(year, month) {
  const start = `${year}-${String(month).padStart(2, "0")}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const end = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  return { start, end };
}

function formatDaywise(dateStr) {
  return moment(dateStr).format("DD-MM-YYYY");
}

function formatPercent(value) {
  if (value === null || value === undefined) return "";
  const rounded = Math.round(value);
  return `${rounded}%`;
}

function sortUsers(users) {
  return [...users].sort((a, b) => {
    const orderA =
      a.jc_sort_order != null
        ? Number(a.jc_sort_order)
        : CUSTOM_ORDER[(a.name || "").toUpperCase()] || 999;
    const orderB =
      b.jc_sort_order != null
        ? Number(b.jc_sort_order)
        : CUSTOM_ORDER[(b.name || "").toUpperCase()] || 999;
    if (orderA !== orderB) return orderA - orderB;
    return (a.name || "").localeCompare(b.name || "");
  });
}

function buildDateRange(start, end) {
  if (!start || !end) return [];
  const dates = [];
  const current = moment(start);
  const last = moment(end);
  while (current.isSameOrBefore(last, "day")) {
    dates.push(current.format("YYYY-MM-DD"));
    current.add(1, "day");
  }
  return dates;
}

export default function Jc_time_report() {
  const { http, user } = AuthUser();
  const now = new Date();
  const defaultMonth = now.getMonth() + 1;
  const defaultYear = now.getFullYear();
  const defaultRange = getMonthRange(defaultYear, defaultMonth);

  const [data, setData] = useState([]);
  const [reportUsers, setReportUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [selectedMonth, setSelectedMonth] = useState(String(defaultMonth));
  const [selectedYear, setSelectedYear] = useState(String(defaultYear));

  useEffect(() => {
    if (startDate && endDate) {
      fetchReport(startDate, endDate);
    }
  }, [startDate, endDate]);

  const fetchReport = (start = "", end = "") => {
    setIsLoading(true);
    const endpoint = `/get_jc_time_workload_report?login_user=${user.id}&startdate=${start}&enddate=${end}`;

    http
      .get(endpoint)
      .then((res) => {
        setIsLoading(false);
        setData(Array.isArray(res.data.data) ? res.data.data : []);
        setReportUsers(Array.isArray(res.data.users) ? res.data.users : []);
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("Error fetching JC Time workload report:", error);
        setData([]);
        setReportUsers([]);
      });
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
    setSelectedMonth("");
    setSelectedYear("");
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
    setSelectedMonth("");
    setSelectedYear("");
  };

  const handleMonthChange = (event) => {
    const month = event.target.value ? parseInt(event.target.value, 10) : "";
    setSelectedMonth(event.target.value);
    if (month && selectedYear) {
      const { start, end } = getMonthRange(parseInt(selectedYear, 10), month);
      setStartDate(start);
      setEndDate(end);
    } else if (!month) {
      setStartDate("");
      setEndDate("");
    }
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    if (year && selectedMonth) {
      const { start, end } = getMonthRange(parseInt(year, 10), parseInt(selectedMonth, 10));
      setStartDate(start);
      setEndDate(end);
    } else if (!year) {
      setStartDate("");
      setEndDate("");
    }
  };

  const handleClear = () => {
    const range = getMonthRange(defaultYear, defaultMonth);
    setStartDate(range.start);
    setEndDate(range.end);
    setSelectedMonth(String(defaultMonth));
    setSelectedYear(String(defaultYear));
  };

  const { dates, people, cellMap, averages } = useMemo(() => {
    const map = {};

    data.forEach((row) => {
      map[`${row.date}|${row.assigned_to_id}`] = row;
    });

    const sortedDates = buildDateRange(startDate, endDate);
    const sortedPeople = sortUsers(
      reportUsers.length
        ? reportUsers
        : [...new Map(data.map((row) => [row.assigned_to_id, { id: row.assigned_to_id, name: row.assigned_to_name }])).values()]
    );

    const avgMap = {};
    sortedPeople.forEach((person) => {
      let sum = 0;
      let count = 0;
      sortedDates.forEach((date) => {
        const cell = map[`${date}|${person.id}`];
        if (!cell || cell.is_leave) return;
        if (cell.percentage > 0) {
          sum += cell.percentage;
          count++;
        }
      });
      avgMap[person.id] = count > 0 ? Math.round(sum / count) : 0;
    });

    return {
      dates: sortedDates,
      people: sortedPeople,
      cellMap: map,
      averages: avgMap,
    };
  }, [data, reportUsers, startDate, endDate]);

  const getCellDisplay = (date, userId) => {
    const cell = cellMap[`${date}|${userId}`];
    if (!cell) return "0%";
    if (cell.is_leave) return "leave";
    return formatPercent(cell.percentage ?? 0);
  };

  const buildTableRows = () => {
    const header = ["DAYWISE", ...people.map((p) => p.name)];
    const body = dates.map((date) => [
      formatDaywise(date),
      ...people.map((person) => getCellDisplay(date, person.id)),
    ]);
    const averageRow = ["Average %", ...people.map((person) => formatPercent(averages[person.id]))];
    return { header, body, averageRow };
  };

  const exportCSV = () => {
    const { header, body, averageRow } = buildTableRows();
    downloadCSV([header, ...body, averageRow], `jc_time_workload_${moment().format("YYYYMMDD_HHmm")}.csv`);
  };

  const exportPDF = () => {
    const { header, body, averageRow } = buildTableRows();
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text("JC Time Workload Report", 14, 15);
    doc.setFontSize(9);
    doc.text(
      `Period: ${formatDaywise(startDate)} to ${formatDaywise(endDate)} | Generated: ${moment().format("DD MMM YYYY, hh:mm A")}`,
      14,
      22
    );

    autoTable(doc, {
      startY: 27,
      head: [header],
      body: [...body, averageRow],
      styles: { fontSize: 7, cellPadding: 2, halign: "center" },
      headStyles: { fillColor: [13, 110, 253], textColor: 255, fontStyle: "bold" },
      columnStyles: { 0: { halign: "left" } },
      didParseCell: (hookData) => {
        if (hookData.section === "body" && hookData.row.index === body.length) {
          hookData.cell.styles.fontStyle = "bold";
          hookData.cell.styles.fillColor = [255, 243, 205];
        }
        if (hookData.section === "body" && hookData.cell.raw === "leave") {
          hookData.cell.styles.textColor = [220, 53, 69];
          hookData.cell.styles.fontStyle = "bold";
        }
      },
    });

    doc.save(`jc_time_workload_${moment().format("YYYYMMDD_HHmm")}.pdf`);
  };

  const years = getYears();

  return (
    <div className="container mt-1">
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <div>
          <h1 className="mb-0">JC Time Workload Report</h1>
          <p className="text-muted mb-0">Daywise work load % — dates as rows, team as columns</p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <Link className="btn btn-sm btn-outline-primary" to="/jobcart/jc_time">
            Back to JC Time
          </Link>
          <button
            className="btn btn-sm btn-outline-success"
            disabled={dates.length === 0}
            onClick={exportCSV}
          >
            CSV
          </button>
          <button
            className="btn btn-sm btn-danger"
            disabled={dates.length === 0}
            onClick={exportPDF}
          >
            PDF
          </button>
        </div>
      </div>

      <div className="row align-items-end">
        <div className="col-md-2 form-group mt-2 mb-2">
          <label className="form-label">Month</label>
          <select className="form-control" value={selectedMonth} onChange={handleMonthChange}>
            <option value="">Select month</option>
            {MONTHS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
        <div className="col-md-2 form-group mt-2 mb-2">
          <label className="form-label">Year</label>
          <select className="form-control" value={selectedYear} onChange={handleYearChange}>
            <option value="">Select year</option>
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div className="col-md-2 form-group mt-2 mb-2">
          <label className="form-label">From Date</label>
          <input
            name="start_date"
            onChange={handleStartDateChange}
            className="form-control"
            type="date"
            value={startDate}
          />
        </div>
        <div className="col-md-2 form-group mt-2 mb-2">
          <label className="form-label">To Date</label>
          <input
            name="end_date"
            onChange={handleEndDateChange}
            className="form-control"
            type="date"
            value={endDate}
          />
        </div>
        <div className="col-md-2 form-group mt-2 mb-2 d-flex align-items-end">
          <button type="button" className="btn btn-outline-secondary" onClick={handleClear}>
            Reset
          </button>
        </div>
      </div>

      <div className="table-responsive">
        {isLoading ? (
          <TaskSkeleton />
        ) : (
          <table className="table table-bordered table-striped center_tbl">
            <thead className="table-primary">
              <tr>
                <th className="daywise-col">DAYWISE</th>
                {people.map((person) => (
                  <th key={person.id} className="text-uppercase text-center">{person.name || "—"}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dates.length === 0 ? (
                <tr>
                  <td colSpan={people.length + 1} className="text-center text-muted">
                    No data for selected period.
                  </td>
                </tr>
              ) : (
                <>
                  {dates.map((date) => (
                    <tr key={date}>
                      <td className="daywise-col fw-medium">{formatDaywise(date)}</td>
                      {people.map((person) => {
                        const cell = cellMap[`${date}|${person.id}`];
                        const display = getCellDisplay(date, person.id);
                        return (
                          <td
                            key={`${date}-${person.id}`}
                            className={`text-center ${cell?.is_leave ? "text-danger fw-bold" : display === "0%" ? "text-muted" : ""}`}
                          >
                            {display}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  <tr className="table-warning fw-bold">
                    <td>Average %</td>
                    {people.map((person) => (
                      <td key={`avg-${person.id}`} className="text-center">
                        {averages[person.id] > 0 ? formatPercent(averages[person.id]) : "0%"}
                      </td>
                    ))}
                  </tr>
                </>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
